import { getSupabase } from '../supabaseClient';
import { fetchAllPages } from './pagination';
import type { SyncCursor } from './syncCursor';
import type { HabitTagMapLocalEntity } from '../localDb/todoLocalDb';

const TABLE_HABIT_TAG_MAP = 'habit_tag_map';
type HabitTagMapStateRow = { tag_id: string; is_deleted: boolean };

export const habitTagRepo = {
  async listMappings(): Promise<HabitTagMapLocalEntity[]> {
    const supabase = getSupabase();
    return await fetchAllPages<HabitTagMapLocalEntity>(() =>
      supabase
        .from(TABLE_HABIT_TAG_MAP)
        .select('habit_id, tag_id, created_at, modified_at, is_deleted')
        .order('modified_at', { ascending: true })
        .order('habit_id', { ascending: true })
        .order('tag_id', { ascending: true })
    );
  },

  async listMappingsModifiedAfter(cursor: SyncCursor): Promise<HabitTagMapLocalEntity[]> {
    const [habitId, tagId] = cursor.tieBreaker;
    return await fetchAllPages<HabitTagMapLocalEntity>(() => {
      const query = getSupabase()
        .from(TABLE_HABIT_TAG_MAP)
        .select('habit_id, tag_id, created_at, modified_at, is_deleted')
        .order('modified_at', { ascending: true })
        .order('habit_id', { ascending: true })
        .order('tag_id', { ascending: true });
      if (!habitId || !tagId) return query.gt('modified_at', cursor.modifiedAt);
      return query.or([
        `modified_at.gt.${cursor.modifiedAt}`,
        `and(modified_at.eq.${cursor.modifiedAt},habit_id.gt.${habitId})`,
        `and(modified_at.eq.${cursor.modifiedAt},habit_id.eq.${habitId},tag_id.gt.${tagId})`,
      ].join(','));
    });
  },

  async setHabitTags(habitId: string, tagIds: string[]): Promise<void> {
    const supabase = getSupabase();
    const unique = Array.from(new Set(tagIds));

    if (unique.length === 0) {
      const { error } = await supabase
        .from(TABLE_HABIT_TAG_MAP)
        .update({ is_deleted: true })
        .eq('habit_id', habitId)
        .eq('is_deleted', false);
      if (error) throw error;
      return;
    }

    const existingRows = await fetchAllPages<HabitTagMapStateRow>(() =>
      supabase
        .from(TABLE_HABIT_TAG_MAP)
        .select('tag_id, is_deleted')
        .eq('habit_id', habitId)
        .order('tag_id', { ascending: true })
    );
    const existingByTagId = new Map(existingRows.map(row => [row.tag_id, row]));
    const desiredTagIds = new Set(unique);

    const rowsToInsert = unique
      .filter(tagId => !existingByTagId.has(tagId))
      .map(tagId => ({
        habit_id: habitId,
        tag_id: tagId,
        is_deleted: false,
      }));
    const tagIdsToRevive = unique.filter(tagId => existingByTagId.get(tagId)?.is_deleted);

    const staleTagIds = existingRows
      .filter(row => !row.is_deleted && !desiredTagIds.has(row.tag_id))
      .map(row => row.tag_id);

    if (rowsToInsert.length > 0) {
      const { error: upsertError } = await supabase
        .from(TABLE_HABIT_TAG_MAP)
        .upsert(rowsToInsert as any, { onConflict: 'habit_id,tag_id' });
      if (upsertError) throw upsertError;
    }

    if (tagIdsToRevive.length > 0) {
      const { error: reviveError } = await supabase
        .from(TABLE_HABIT_TAG_MAP)
        .update({ is_deleted: false })
        .eq('habit_id', habitId)
        .in('tag_id', tagIdsToRevive);
      if (reviveError) throw reviveError;
    }

    if (staleTagIds.length > 0) {
      const { error: deleteExtraError } = await supabase
        .from(TABLE_HABIT_TAG_MAP)
        .update({ is_deleted: true })
        .eq('habit_id', habitId)
        .eq('is_deleted', false)
        .in('tag_id', staleTagIds);
      if (deleteExtraError) throw deleteExtraError;
    }
  },
};
